// CSS pro banner + modál. Vkládá se do Shadow DOM, takže neovlivní hostující
// stránku a naopak. Barvy jsou řízené CSS proměnnými z theme konfigurace.
import type { ThemeConfig } from './types';

function themeVars(theme: ThemeConfig) {
  return {
    primary: theme.primary ?? '#2f6fdb',
    primaryText: theme.primaryText ?? '#ffffff',
    background: theme.background ?? '#ffffff',
    text: theme.text ?? '#1a1d21',
    muted: theme.muted ?? '#5b636b',
    border: theme.border ?? '#e2e6ea',
    radius: theme.radius ?? '12px',
  };
}

/** Styly „Privacy trigger" widgetu — vlastní shadow root na stránce (privacy page). */
export function buildTriggerStyles(theme: ThemeConfig): string {
  const t = themeVars(theme);
  return `
:host { display: block; }
*, *::before, *::after { box-sizing: border-box; }
.cg-trigger {
  --cg-primary: ${t.primary}; --cg-primary-text: ${t.primaryText};
  --cg-bg: ${t.background}; --cg-text: ${t.text}; --cg-muted: ${t.muted};
  --cg-border: ${t.border}; --cg-radius: ${t.radius};
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px; line-height: 1.5; color: var(--cg-text); background: var(--cg-bg);
  border: 1px solid var(--cg-border); border-radius: var(--cg-radius);
  padding: 16px 18px;
}
.cg-trig-title { font-weight: 700; font-size: 15px; margin: 0 0 12px; }
.cg-trig-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 18px; }
.cg-trig-field { min-width: 0; }
.cg-trig-label { font-size: 12px; color: var(--cg-muted); margin-bottom: 2px; }
.cg-trig-value { font-weight: 600; word-break: break-word; }
.cg-trig-id { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px; font-weight: 500; }
.cg-trig-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.cg-trig-chip { font-size: 12px; padding: 2px 9px; border-radius: 999px; border: 1px solid var(--cg-border); color: var(--cg-muted); }
.cg-trig-chip.on { background: var(--cg-primary); border-color: var(--cg-primary); color: var(--cg-primary-text); }
.cg-trig-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 16px; }
.cg-trig-btn { border: 1px solid var(--cg-border); background: var(--cg-bg); color: var(--cg-text);
  padding: 8px 14px; border-radius: 8px; font-weight: 600; cursor: pointer; font-family: inherit; font-size: 14px; }
.cg-trig-btn:hover { background: rgba(0,0,0,.04); }
.cg-trig-btn.primary { background: var(--cg-primary); border-color: var(--cg-primary); color: var(--cg-primary-text); }
.cg-trig-btn.primary:hover { filter: brightness(.94); }
.cg-trig-none { color: var(--cg-muted); margin: 0 0 12px; }
.cg-trig-details { margin-top: 16px; }
.cg-trig-text { margin: 0 0 10px; }
.cg-trig-cat { padding: 14px 0 0; }
.cg-trig-empty { font-size: 12px; color: var(--cg-muted); font-style: italic; margin: 6px 0 0; }
.cg-trig-table-wrap { overflow-x: auto; margin-top: 8px; }
/* Pevné šířky sloupců — tabulky všech kategorií pod sebou lícují. Na úzkém
   displeji drží min-width a obal scrolluje vodorovně. */
.cg-trig-table { width: 100%; min-width: 640px; table-layout: fixed; border-collapse: collapse; font-size: 12px; }
.cg-trig-table th:nth-child(1) { width: 20%; }
.cg-trig-table th:nth-child(2) { width: 16%; }
.cg-trig-table th:nth-child(4) { width: 11%; }
.cg-trig-table th:nth-child(5) { width: 12%; }
.cg-trig-table th, .cg-trig-table td { text-align: left; vertical-align: top; padding: 6px 8px; border-bottom: 1px solid var(--cg-border); }
.cg-trig-table th { color: var(--cg-muted); font-weight: 600; white-space: nowrap; }
.cg-trig-cookie { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; word-break: break-all; }
`;
}

export function buildStyles(theme: ThemeConfig): string {
  const t = themeVars(theme);

  return `
:host {
  --cg-primary: ${t.primary};
  --cg-primary-text: ${t.primaryText};
  --cg-bg: ${t.background};
  --cg-text: ${t.text};
  --cg-muted: ${t.muted};
  --cg-border: ${t.border};
  --cg-radius: ${t.radius};
  all: initial;
}
*, *::before, *::after { box-sizing: border-box; }
.cg-root {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px; line-height: 1.5; color: var(--cg-text);
}
button { font-family: inherit; font-size: 14px; cursor: pointer; }

/* Overlay */
.cg-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  z-index: 2147483646; animation: cg-fade .2s ease;
}
@keyframes cg-fade { from { opacity: 0 } to { opacity: 1 } }

/* Banner (první vrstva) */
.cg-banner {
  position: fixed; z-index: 2147483647; background: var(--cg-bg);
  border: 1px solid var(--cg-border); border-radius: var(--cg-radius);
  box-shadow: 0 8px 40px rgba(0,0,0,.18); padding: 22px 24px;
  max-width: 460px; animation: cg-slide .25s ease;
}
.cg-pos-bottom { left: 50%; bottom: 22px; transform: translateX(-50%); }
.cg-pos-bottom-left { left: 22px; bottom: 22px; }
.cg-pos-bottom-right { right: 22px; bottom: 22px; }
.cg-pos-center { left: 50%; top: 50%; transform: translate(-50%,-50%); }
@keyframes cg-slide { from { opacity: 0; transform: translateY(12px) } }
.cg-pos-bottom.cg-banner { animation: none; }

.cg-logo { display: block; max-height: 40px; max-width: 180px; width: auto; margin: 0 0 12px; }
.cg-title { font-size: 17px; font-weight: 700; margin: 0 0 8px; }
.cg-body { color: var(--cg-muted); margin: 0 0 18px; }
.cg-body a { color: var(--cg-primary); }
.cg-actions { display: flex; flex-wrap: wrap; gap: 10px; }

.cg-btn {
  border: 1px solid var(--cg-border); background: var(--cg-bg);
  color: var(--cg-text); padding: 10px 18px; border-radius: 8px;
  font-weight: 600; transition: background .15s, border-color .15s;
}
.cg-btn:hover { background: rgba(0,0,0,.04); }
.cg-btn-primary {
  background: var(--cg-primary); border-color: var(--cg-primary);
  color: var(--cg-primary-text); flex: 1 0 auto;
}
.cg-btn-primary:hover { filter: brightness(.94); background: var(--cg-primary); }
.cg-btn-link {
  border: none; background: none; color: var(--cg-primary);
  padding: 10px 4px; text-decoration: underline;
}

/* Modál (druhá vrstva) */
.cg-modal {
  position: fixed; z-index: 2147483647; left: 50%; top: 50%;
  transform: translate(-50%,-50%); background: var(--cg-bg);
  border-radius: var(--cg-radius); box-shadow: 0 10px 50px rgba(0,0,0,.25);
  width: min(560px, calc(100vw - 32px)); max-height: calc(100vh - 48px);
  display: flex; flex-direction: column; overflow: hidden;
}
.cg-modal-head { padding: 22px 24px 8px; }
.cg-modal-head h2 { margin: 0 0 6px; font-size: 18px; }
.cg-modal-intro { color: var(--cg-muted); margin: 0; }
.cg-modal-body { padding: 8px 24px; overflow-y: auto; }

.cg-cat {
  border-top: 1px solid var(--cg-border); padding: 16px 0;
}
.cg-cat-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.cg-cat-name { font-weight: 700; font-size: 15px; }
.cg-cat-desc { color: var(--cg-muted); margin: 8px 0 0; }

/* Přepínač */
.cg-switch { position: relative; width: 46px; height: 26px; flex: 0 0 auto; }
.cg-switch input { position: absolute; opacity: 0; width: 100%; height: 100%; margin: 0; cursor: pointer; }
.cg-track {
  position: absolute; inset: 0; background: #cfd4da; border-radius: 999px;
  transition: background .18s;
}
.cg-thumb {
  position: absolute; top: 3px; left: 3px; width: 20px; height: 20px;
  background: #fff; border-radius: 50%; transition: transform .18s;
  box-shadow: 0 1px 3px rgba(0,0,0,.3);
}
.cg-switch input:checked + .cg-track { background: var(--cg-primary); }
.cg-switch input:checked + .cg-track .cg-thumb { transform: translateX(20px); }
.cg-switch input:disabled { cursor: not-allowed; }
.cg-switch input:disabled + .cg-track { background: var(--cg-primary); opacity: .5; }
.cg-switch input:focus-visible + .cg-track { outline: 2px solid var(--cg-primary); outline-offset: 2px; }

.cg-cat-always { font-size: 12px; color: var(--cg-primary); font-weight: 600; flex: 0 0 auto; }

/* Detail cookies */
.cg-cookies { margin-top: 12px; }
.cg-cookies summary { cursor: pointer; color: var(--cg-primary); font-size: 13px; }
.cg-cookie-table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 12px; }
.cg-cookie-table th, .cg-cookie-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--cg-border); }
.cg-cookie-table th { color: var(--cg-muted); font-weight: 600; }

.cg-modal-foot {
  padding: 16px 24px; border-top: 1px solid var(--cg-border);
  display: flex; flex-wrap: wrap; gap: 10px; justify-content: flex-end;
}
.cg-modal-foot .cg-btn-primary { flex: 0 0 auto; }
.cg-spacer { flex: 1 1 auto; }

/* Plovoucí ikonka pro odvolání — brandová primární barva, kontrastní v každém
   tématu (dřív brala --cg-bg, u tmavého tématu = černý nečitelný kruh). */
.cg-reopen {
  position: fixed; left: 18px; bottom: 18px; z-index: 2147483645;
  width: 46px; height: 46px; border-radius: 50%; border: none;
  background: var(--cg-primary); color: var(--cg-primary-text);
  box-shadow: 0 4px 16px rgba(0,0,0,.28);
  display: grid; place-items: center; padding: 0;
  transition: filter .15s;
}
.cg-reopen:hover { filter: brightness(.94); }
.cg-reopen svg { width: 24px; height: 24px; }

@media (max-width: 520px) {
  .cg-banner { left: 12px; right: 12px; bottom: 12px; max-width: none; transform: none; }
  .cg-actions { flex-direction: column; }
  .cg-btn, .cg-btn-primary { width: 100%; }
}
`;
}
